import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Search, Filter, ShoppingBag, Sparkles, SlidersHorizontal, ArrowUpDown, RefreshCw, CheckCircle2, ShieldCheck, Truck, RotateCcw, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import ProductCard from '../components/cards/ProductCard';

import API from '../config/api';

const DEFAULT_CATEGORIES = [
  { id: 'all', slug: 'all', name: 'All Offerings', badge: 'Popular' },
  { id: 'bracelets', slug: 'bracelets', name: 'Dhan Yog Bracelets', badge: 'Featured' },
  { id: 'rudraksha', slug: 'rudraksha', name: 'Rudraksha & Malas' },
  { id: 'pendants', slug: 'pendants', name: 'Sacred Pendants' },
  { id: 'ghee', slug: 'ghee', name: 'Pure Badri Ghee' },
  { id: 'dal', slug: 'dal', name: 'Organic Dals' },
  { id: 'yantra', slug: 'yantra', name: 'Copper Yantras' },
  { id: 'ganga_jal', slug: 'ganga_jal', name: 'Gangotri Jal' },
  { id: 'idols', slug: 'idols', name: 'Brass Idols' },
  { id: 'keeda_jadi', slug: 'keeda_jadi', name: 'Himalayan Herbs' },
];

const StorePage = () => {
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortOption, setSortOption] = useState('default');
  
  // Mobile Filter Modal states
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempCategory, setTempCategory] = useState('all');
  const [tempSort, setTempSort] = useState('default');

  const openFilterModal = () => {
    setTempCategory(activeCategory);
    setTempSort(sortOption);
    setIsFilterModalOpen(true);
  };
  
  // Pagination on scroll states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);

  const observerTarget = useRef(null);

  // Fetch dynamic categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        const catList = Array.isArray(response.data) ? response.data : [];
        if (catList.length > 0) {
          setCategories([
            { id: 'all', slug: 'all', name: 'All Offerings', badge: 'Popular' },
            ...catList.map(c => ({
              id: c.slug || c.id,
              slug: c.slug || c.id,
              name: c.name,
              badge: c.badge || ''
            }))
          ]);
        }
      } catch (err) {
        console.error('Error fetching dynamic categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch initial batch when filters or sorting change
  const fetchProducts = useCallback(async (pageNum = 1, isAppend = false) => {
    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await axios.get(`${API}/products`, {
        params: {
          page: pageNum,
          limit: 8,
          category: activeCategory === 'all' ? '' : activeCategory,
          search: debouncedSearch,
          sort: sortOption
        }
      });

      const data = response.data;
      
      let newItems = [];
      let totalCount = 0;
      let moreAvailable = false;

      if (data && Array.isArray(data.products)) {
        newItems = data.products;
        totalCount = data.total || data.products.length;
        moreAvailable = data.hasMore !== undefined ? data.hasMore : (data.products.length === 8);
      } else if (Array.isArray(data)) {
        // Fallback for plain array response
        newItems = data.slice((pageNum - 1) * 8, pageNum * 8);
        totalCount = data.length;
        moreAvailable = pageNum * 8 < totalCount;
      }

      if (isAppend) {
        setProducts(prev => [...prev, ...newItems]);
      } else {
        setProducts(newItems);
      }

      setTotalProducts(totalCount);
      setHasMore(moreAvailable);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeCategory, debouncedSearch, sortOption]);

  // Reset pagination when category, search, or sort changes
  useEffect(() => {
    setPage(1);
    fetchProducts(1, false);
  }, [activeCategory, debouncedSearch, sortOption, fetchProducts]);

  // Handle scroll intersection to load next page
  const loadNextPage = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProducts(nextPage, true);
    }
  }, [loading, loadingMore, hasMore, page, fetchProducts]);

  // Setup Intersection Observer for scroll pagination
  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadNextPage();
        }
      },
      { threshold: 0.2, rootMargin: '200px' }
    );

    observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [observerTarget, hasMore, loading, loadingMore, loadNextPage]);

  return (
    <>
      <Navbar />
      
      <div className="min-h-screen bg-[#FBF4E7]">
        {/* ═══════════════════ COMPACT HERO BANNER (HIDDEN ON MOBILE DEVICE) ═══════════════════ */}
        <div className="hidden md:block relative pt-24 pb-10 md:pt-28 md:pb-12 bg-[#4E1225] text-white overflow-hidden shadow-md">
          {/* Background Image with Dark Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1600010476008-8e684074bba6?q=80&w=2000"
              alt="Himalayan Sacred Background"
              className="w-full h-full object-cover"
            />
            {/* Rich Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#4E1225]/95 via-[#4E1225]/85 to-[#4E1225]/95 backdrop-blur-[2px]"></div>
          </div>
          
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#B98B3E]/40 bg-[#B98B3E]/15 backdrop-blur-md mb-3">
                <Sparkles className="w-3.5 h-3.5 text-[#F2A65A]" />
                <span className="text-[#E8D5A8] font-bold text-[11px] uppercase tracking-[0.2em]">Paramparik Divine Store</span>
              </div>
              
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#FBF4E7] mb-2 tracking-tight">
                Authentic <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F2A65A] via-[#B98B3E] to-[#E8D5A8]">Vedic &amp; Pahadi</span> Offerings
              </h1>
              
              <p className="text-[#E8D5A8]/90 text-xs sm:text-sm font-normal max-w-xl mx-auto mb-6 leading-relaxed">
                Energised Dhan Yog bracelets, Nepal Rudraksha malas, pure Badri A2 ghee &amp; sacred items sourced straight from Uttarakhand.
              </p>

              {/* Search Bar */}
              <div className="max-w-xl mx-auto relative">
                <div className="relative flex items-center bg-white/95 backdrop-blur-xl rounded-2xl p-1 shadow-xl border border-[#B98B3E]/30">
                  <Search className="w-4 h-4 text-[#83725B] ml-3 flex-shrink-0" />
                  <Input
                    placeholder="Search Dhan Yog bracelet, Rudraksha, Badri ghee..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent border-0 text-[#2A1F16] placeholder-[#83725B]/70 focus:ring-0 text-xs sm:text-sm px-3 h-10 shadow-none"
                    data-testid="product-search-input"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="text-xs text-[#83725B] hover:text-[#4E1225] px-2 font-medium"
                    >
                      Clear
                    </button>
                  )}
                  <button className="bg-[#6E1B34] hover:bg-[#4E1225] text-white px-4 sm:px-5 h-9 rounded-xl font-semibold text-xs transition-all shadow-md flex-shrink-0 ml-1">
                    Search
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ═══════════════════ MOBILE TOP SEARCH BAR & FILTER ICON ═══════════════════ */}
        <div className="md:hidden pt-20 pb-3.5 px-4 bg-[#4E1225] text-white shadow-md">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 flex items-center bg-white rounded-xl p-1 shadow-sm border border-[#E7D9BC]">
              <Search className="w-4 h-4 text-[#83725B] ml-2.5 flex-shrink-0" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-0 text-[#2A1F16] placeholder-[#83725B]/70 focus:ring-0 text-xs px-2.5 h-9 shadow-none"
                data-testid="product-search-input-mobile"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="text-xs text-[#83725B] hover:text-[#4E1225] px-2 font-medium"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Filter Icon Button */}
            <button
              type="button"
              onClick={openFilterModal}
              className={`p-2.5 rounded-xl border flex items-center justify-center transition-all flex-shrink-0 shadow-sm ${
                activeCategory !== 'all' || sortOption !== 'default'
                  ? 'bg-[#D9711F] text-white border-[#D9711F]'
                  : 'bg-white text-[#4E1225] border-[#E7D9BC]'
              }`}
              aria-label="Filter & Sort"
              data-testid="mobile-filter-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
            </button>
          </div>

          {/* Active Filter Badges on Mobile */}
          {(activeCategory !== 'all' || sortOption !== 'default') && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2.5 text-[11px]">
              {activeCategory !== 'all' && (
                <span className="inline-flex items-center gap-1 bg-[#D9711F] text-white px-2.5 py-0.5 rounded-full font-medium shadow-2xs">
                  {categories.find(c => c.id === activeCategory)?.name || activeCategory}
                  <button onClick={() => setActiveCategory('all')} className="hover:text-amber-200 ml-0.5">✕</button>
                </span>
              )}
              {sortOption !== 'default' && (
                <span className="inline-flex items-center gap-1 bg-[#B98B3E] text-white px-2.5 py-0.5 rounded-full font-medium shadow-2xs">
                  {sortOption === 'price_asc' ? 'Price: Low to High' : sortOption === 'price_desc' ? 'Price: High to Low' : 'Top Rated'}
                  <button onClick={() => setSortOption('default')} className="hover:text-amber-200 ml-0.5">✕</button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* ═══════════════════ MAIN CONTENT AREA ═══════════════════ */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-10">
          
          {/* Category Filter Horizontal Scroll (Desktop Only) */}
          <div className="hidden md:block mb-6 sm:mb-8 overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-base sm:text-lg font-bold text-[#4E1225] flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#D9711F]" />
                Browse Categories
              </h2>
            </div>
            
            <div className="flex items-center gap-2 overflow-x-auto pb-3 pt-1 scrollbar-none">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-200 flex items-center gap-1.5 border ${
                    activeCategory === cat.id
                      ? 'bg-[#6E1B34] text-white border-[#6E1B34] shadow-md scale-[1.02]'
                      : 'bg-white text-[#2A1F16] border-[#E7D9BC] hover:border-[#D9711F]/50 hover:bg-[#F3E7D0]/50'
                  }`}
                  data-testid={`product-category-${cat.id}`}
                >
                  {cat.name}
                  {cat.badge && (
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                      activeCategory === cat.id ? 'bg-[#D9711F] text-white' : 'bg-[#F3E7D0] text-[#6E1B34]'
                    }`}>
                      {cat.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Filter & Sorting Controls Strip (Desktop Only) */}
          <div className="hidden md:flex flex-col sm:flex-row sm:items-center justify-between bg-white rounded-2xl p-3.5 sm:p-4 mb-6 sm:mb-8 border border-[#E7D9BC] shadow-xs gap-3 sm:gap-4">
            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-[#83725B]">
              <span className="font-bold text-[#4E1225] text-sm sm:text-base">{totalProducts}</span>
              <span>{totalProducts === 1 ? 'item available' : 'sacred items available'}</span>
              {activeCategory !== 'all' && (
                <span className="bg-[#F3E7D0] text-[#6E1B34] px-2 py-0.5 rounded-md text-xs font-semibold">
                  Category: {categories.find(c => c.id === activeCategory)?.name}
                </span>
              )}
              {debouncedSearch && (
                <span className="bg-[#F3E7D0] text-[#6E1B34] px-2 py-0.5 rounded-md text-xs font-semibold">
                  Query: "{debouncedSearch}"
                </span>
              )}
            </div>

            <div className="flex items-center gap-2.5 sm:gap-3 self-end sm:self-auto">
              <span className="text-xs font-semibold text-[#83725B] flex items-center gap-1">
                <ArrowUpDown className="w-3.5 h-3.5" /> Sort:
              </span>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="bg-[#FBF4E7] border border-[#E7D9BC] text-[#2A1F16] text-xs sm:text-sm rounded-xl px-3 py-2 font-medium focus:outline-none focus:border-[#D9711F]"
                data-testid="product-sort-select"
              >
                <option value="default">Featured &amp; Bestsellers</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="rating">Top Rated</option>
              </select>
            </div>
          </div>

          {/* Mobile Items Count and Filter Button Bar */}
          <div className="md:hidden flex items-center justify-between px-1 mb-4 text-xs text-[#83725B]">
            <span className="font-medium">
              <strong className="text-[#4E1225] font-bold">{totalProducts}</strong> sacred items available
            </span>
            <button
              type="button"
              onClick={openFilterModal}
              className="flex items-center gap-1.5 text-[#6E1B34] font-bold bg-white px-3 py-1.5 rounded-xl border border-[#E7D9BC] shadow-2xs active:scale-95 transition-all"
            >
              <Filter className="w-3.5 h-3.5 text-[#D9711F]" />
              <span>Filter / Sort</span>
            </button>
          </div>

          {/* ═══════════════════ PRODUCTS GRID (1 PRODUCT ON MOBILE VIEW, 2 ON SM, 3 ON MD, 4 ON LG) ═══════════════════ */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {Array(8).fill(0).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl h-80 sm:h-88 border border-[#E7D9BC]/50 p-4 animate-pulse flex flex-col justify-between">
                  <div className="w-full h-48 bg-[#F3E7D0] rounded-xl mb-3"></div>
                  <div className="h-4 bg-[#F3E7D0] rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-[#F3E7D0] rounded w-1/2 mb-3"></div>
                  <div className="h-10 bg-[#F3E7D0] rounded-xl w-full"></div>
                </div>
              ))}
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
                {products.map((product, index) => (
                  <ProductCard key={`${product.id}-${index}`} product={product} index={index} />
                ))}
              </div>

              {/* ═══════════════════ SCROLL PAGINATION SENTINEL & SPINNER ═══════════════════ */}
              <div ref={observerTarget} className="py-8 text-center flex flex-col items-center justify-center min-h-[100px]">
                {loadingMore && (
                  <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-full shadow-md border border-[#E7D9BC]">
                    <RefreshCw className="w-5 h-5 text-[#D9711F] animate-spin" />
                    <span className="text-xs sm:text-sm font-semibold text-[#4E1225]">
                      Loading more sacred offerings...
                    </span>
                  </div>
                )}

                {!hasMore && products.length >= 8 && (
                  <div className="text-center py-6 border-t border-[#E7D9BC] w-full max-w-md">
                    <p className="text-xs sm:text-sm font-semibold text-[#83725B] flex items-center justify-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-[#2E7D45]" />
                      You have explored all {products.length} offerings in this collection.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-3xl p-8 sm:p-12 text-center border border-[#E7D9BC] my-8 shadow-xs">
              <ShoppingBag className="w-14 h-14 sm:w-16 sm:h-16 text-[#B98B3E] mx-auto mb-4 opacity-50" />
              <h3 className="font-heading text-lg sm:text-xl font-bold text-[#4E1225] mb-2">
                No items match your criteria
              </h3>
              <p className="text-[#83725B] text-xs sm:text-sm max-w-md mx-auto mb-6">
                Try clearing your search query or selecting a different category from above.
              </p>
              <button
                onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
                className="bg-[#6E1B34] text-white px-6 py-2.5 rounded-xl text-xs font-semibold shadow-md hover:bg-[#4E1225] transition-colors"
              >
                Reset All Filters
              </button>
            </div>
          )}

          {/* ═══════════════════ TRUST ASSURANCE BANNER ═══════════════════ */}
          <div className="mt-12 sm:mt-16 bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[#E7D9BC] shadow-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 text-center">
              <div className="flex flex-col items-center p-2 sm:p-3">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#F3E7D0] text-[#6E1B34] flex items-center justify-center mb-2.5 sm:mb-3">
                  <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <h4 className="font-heading font-bold text-sm text-[#4E1225] mb-1">100% Authentic</h4>
                <p className="text-xs text-[#83725B]">Directly sourced from Uttarakhand &amp; Vedic hubs</p>
              </div>

              <div className="flex flex-col items-center p-2 sm:p-3">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#F3E7D0] text-[#6E1B34] flex items-center justify-center mb-2.5 sm:mb-3">
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-[#D9711F]" />
                </div>
                <h4 className="font-heading font-bold text-sm text-[#4E1225] mb-1">Vedic Prana Pratishtha</h4>
                <p className="text-xs text-[#83725B]">Energised by qualified Vedic Pandits prior to dispatch</p>
              </div>

              <div className="flex flex-col items-center p-2 sm:p-3">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#F3E7D0] text-[#6E1B34] flex items-center justify-center mb-2.5 sm:mb-3">
                  <Truck className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <h4 className="font-heading font-bold text-sm text-[#4E1225] mb-1">Pan-India Express</h4>
                <p className="text-xs text-[#83725B]">Free shipping on orders above ₹999 with tracking</p>
              </div>

              <div className="flex flex-col items-center p-2 sm:p-3">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#F3E7D0] text-[#6E1B34] flex items-center justify-center mb-2.5 sm:mb-3">
                  <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6 text-[#2E7D45]" />
                </div>
                <h4 className="font-heading font-bold text-sm text-[#4E1225] mb-1">7-Day Easy Returns</h4>
                <p className="text-xs text-[#83725B]">Hassle-free replacement guarantee</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ═══════════════════ MOBILE FILTER & SORT MODAL ═══════════════════ */}
      <AnimatePresence>
        {isFilterModalOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsFilterModalOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-xs"
            />

            {/* Modal Body */}
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 26, stiffness: 280 }}
              className="relative z-10 w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#E7D9BC] bg-[#FBF4E7]">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-[#D9711F]" />
                  <h3 className="font-heading text-base font-bold text-[#4E1225]">
                    Filter &amp; Sort Products
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setIsFilterModalOpen(false)}
                  className="w-8 h-8 rounded-full bg-white text-[#83725B] hover:text-[#4E1225] flex items-center justify-center border border-[#E7D9BC] transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="p-5 overflow-y-auto space-y-6 flex-1">
                {/* 1. Sort Options */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#83725B] block mb-2.5">
                    Sort By
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: 'default', label: 'Featured & Bestsellers' },
                      { id: 'price_asc', label: 'Price: Low to High' },
                      { id: 'price_desc', label: 'Price: High to Low' },
                      { id: 'rating', label: 'Top Rated' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setTempSort(opt.id)}
                        className={`p-2.5 rounded-xl text-xs font-medium text-left border transition-all ${
                          tempSort === opt.id
                            ? 'bg-[#6E1B34] text-white border-[#6E1B34] shadow-xs font-bold'
                            : 'bg-white text-[#2A1F16] border-[#E7D9BC] hover:bg-[#F3E7D0]/40'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 2. Browse Categories */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#83725B] block mb-2.5">
                    Browse Categories
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setTempCategory(cat.id)}
                        className={`p-2.5 rounded-xl text-xs font-medium text-left border transition-all flex items-center justify-between ${
                          tempCategory === cat.id
                            ? 'bg-[#6E1B34] text-white border-[#6E1B34] shadow-xs font-bold'
                            : 'bg-white text-[#2A1F16] border-[#E7D9BC] hover:bg-[#F3E7D0]/40'
                        }`}
                      >
                        <span className="truncate">{cat.name}</span>
                        {cat.badge && (
                          <span className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold ml-1 ${
                            tempCategory === cat.id ? 'bg-[#D9711F] text-white' : 'bg-[#F3E7D0] text-[#6E1B34]'
                          }`}>
                            {cat.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-4 border-t border-[#E7D9BC] bg-[#FBF4E7] flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setTempCategory('all');
                    setTempSort('default');
                  }}
                  className="flex-1 py-3 rounded-xl border border-[#6E1B34] text-[#6E1B34] font-bold text-xs hover:bg-[#F3E7D0] transition-colors"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveCategory(tempCategory);
                    setSortOption(tempSort);
                    setIsFilterModalOpen(false);
                  }}
                  className="flex-1 py-3 rounded-xl bg-[#6E1B34] text-white font-bold text-xs hover:bg-[#4E1225] shadow-md transition-colors"
                  data-testid="apply-filter-modal-btn"
                >
                  Apply Filters
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <Footer />
    </>
  );
};

export default StorePage;
